import { GovernmentIdInformation } from "@/types";
import {
  CoreFieldProps,
  Countries,
  EnumSingleSelectFieldV2Props,
  fieldTypes,
  SectionHeader,
} from "@ucl/library";
import { FC } from "react";
import { FieldComplexV2 } from "./FieldComplexV2";
import { COMPLEX_FIELD_KEYS } from "@/constants/fieldKeys";
import "./ComplexFields.scss";
import { GovernmentIdType, ReasonPhysicalAddressIsDifferentThanIdAddressType } from "@/types/enums";

export interface GovernmentIdInformationComplexValue {
  showHeader?: boolean;
  readOnly?: boolean;
  headerLabel?: string;
  shouldAskIfCanProvideGovernmentIdInformation?: boolean;
  canProvideGovernmentIdInformation?: boolean;
  shouldAlwaysShowGovernmentIdInformationFields?: boolean;
}

export interface GovernmentIdInformationComplexFieldProps
  extends fieldTypes.BaseFieldProps<GovernmentIdInformation> {
  complexFieldValue?: GovernmentIdInformationComplexValue;
}

export const GovernmentIdInformationComplexField: FC<GovernmentIdInformationComplexFieldProps> = (
  props
) => {
  const readOnly = props.complexFieldValue?.readOnly || props.readOnly;
  const showHeader = props.complexFieldValue?.showHeader;

  const shouldShowGovernmentIdInformationFields =
    props.complexFieldValue?.shouldAskIfCanProvideGovernmentIdInformation !== true ||
    props.complexFieldValue?.canProvideGovernmentIdInformation === true ||
    props.complexFieldValue?.shouldAlwaysShowGovernmentIdInformationFields === true;

  return (
    <section className="complex-field-container government-id-information">
      <SectionHeader
        label={props.complexFieldValue?.headerLabel || props.label}
        hidden={!showHeader}
      />
      <div className="complex-field-content">
        {props.complexFieldValue?.shouldAskIfCanProvideGovernmentIdInformation && (
          <FieldComplexV2
            {...props}
            complexFieldKey={
              COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.CAN_PROVIDE_GOVERNMENT_ID_INFORMATION
            }
            complexFieldProps={
              { disabled: readOnly, showClearButton: true } as Partial<CoreFieldProps>
            }
          />
        )}
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.IS_US_CITIZEN}
          complexFieldProps={
            {
              disabled: readOnly,
              showClearButton: true,
              hidden: !shouldShowGovernmentIdInformationFields,
            } as Partial<CoreFieldProps>
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.GOVERNMENT_ID_TYPE}
          complexFieldProps={
            {
              disabled: readOnly,
              showClearButton: true,
              hidden: !shouldShowGovernmentIdInformationFields,
            } as Partial<CoreFieldProps>
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.GOVERNMENT_ID_NUMBER}
          complexFieldProps={{
            disabled: readOnly,
            hidden: !shouldShowGovernmentIdInformationFields,
          }}
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.GOVERNMENT_ID_ISSUER_STATE}
          complexFieldProps={{
            hidden:
              !shouldShowGovernmentIdInformationFields ||
              props.value?.governmentIdType !== GovernmentIdType.StateIssuedID,
            disabled: readOnly,
          }}
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={
            COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.GOVERNMENT_ID_ISSUER_COUNTRY
          }
          complexFieldProps={
            {
              hidden:
                !shouldShowGovernmentIdInformationFields ||
                props.value?.governmentIdType !== GovernmentIdType.Passport,
              disabled: readOnly,
              sortFunction: (x, y) => {
                // Put "United States" at the top
                if (x.value === Countries.UnitedStates) return -1;
                if (y.value === Countries.UnitedStates) return 1;
                // Sort everything else alphabetically
                return x.label.localeCompare(y.label);
              },
            } as EnumSingleSelectFieldV2Props
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.GOVERNMENT_ID_EXPIRY}
          complexFieldProps={{
            disabled: readOnly,
            hidden: !shouldShowGovernmentIdInformationFields,
          }}
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={
            COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION
              .IS_PHYSICAL_ADDRESS_DIFFERENT_THAN_ID_ADDRESS
          }
          complexFieldProps={
            {
              disabled: readOnly,
              showClearButton: true,
              hidden: !shouldShowGovernmentIdInformationFields || 
                props.value?.governmentIdType === GovernmentIdType.Passport,
            } as Partial<CoreFieldProps>
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={
            COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION
              .REASON_PHYSICAL_ADDRESS_IS_DIFFERENT_THAN_ID_ADDRESS
          }
          complexFieldProps={
            {
              hidden:
                !shouldShowGovernmentIdInformationFields ||
                !props.value?.isPhysicalAddressDifferentThanIdAddress ||
                props.value?.governmentIdType === GovernmentIdType.Passport,
              disabled: readOnly,
            } as Partial<CoreFieldProps>
          }
        />
        <FieldComplexV2
          {...props}
          complexFieldKey={
            COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION
              .OTHER_REASON_PHYSICAL_ADDRESS_IS_DIFFERENT_THAN_ID_ADDRESS
          }
          complexFieldProps={
            {
              hidden:
                !shouldShowGovernmentIdInformationFields ||
                !props.value?.isPhysicalAddressDifferentThanIdAddress ||
                props.value?.reasonPhysicalAddressIsDifferentThanIdAddress !==
                  ReasonPhysicalAddressIsDifferentThanIdAddressType.Other ||
                props.value?.governmentIdType === GovernmentIdType.Passport,
              disabled: readOnly,
            } as Partial<CoreFieldProps>
          }
        />
      </div>
    </section>
  );
};
